import React, { useState, useEffect, useRef } from 'react';
import {
  Input, Tag, Spin, Empty, Badge, Button, Drawer, Modal, message,
} from 'antd';
import {
  SearchOutlined, ShoppingCartOutlined, PlusOutlined,
  MinusOutlined, DeleteOutlined, CheckOutlined, UserOutlined, ClearOutlined,
  BarcodeOutlined,
} from '@ant-design/icons';
import { db } from '../firebase';
import { collection, getDocs, addDoc, Timestamp, query, where } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { BrowserMultiFormatReader } from '@zxing/browser';

export default function BuvetteMenu() {
  const auth = getAuth();
  const currentUser = auth.currentUser;
  const navigate = useNavigate();

  const [snacks, setSnacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [imgErrors, setImgErrors] = useState({});
  const [clientName, setClientName] = useState('');
  const [firebaseId, setFirebaseId] = useState('');
  const [cart, setCart] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [successVisible, setSuccessVisible] = useState(false);
  const [lastReceipt, setLastReceipt] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannerActive, setScannerActive] = useState(false);
  const [lastScanned, setLastScanned] = useState(null);
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const cooldownRef = useRef(false);

  useEffect(() => { if (!currentUser) navigate('/login'); }, [currentUser, navigate]);

  useEffect(() => {
    const fetchUser = async () => {
      if (!currentUser?.uid) return;
      try {
        const snap = await getDocs(query(collection(db, 'contact'), where('FirebaseId', '==', currentUser.uid)));
        if (!snap.empty) {
          const data = snap.docs[0].data();
          setClientName(data.fullname || data.name || '');
          setFirebaseId(currentUser.uid);
        }
      } catch (e) { console.error(e); }
    };
    fetchUser();
  }, [currentUser]);

  useEffect(() => {
    const fetchSnacks = async () => {
      try {
        const snap = await getDocs(collection(db, 'snacks'));
        const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
          .filter((s) => s.available !== false).sort((a, b) => a.id.localeCompare(b.id));
        setSnacks(items);
      } catch (e) { console.error(e); } finally { setLoading(false); }
    };
    fetchSnacks();
  }, []);

  const startScanner = () => { setLastScanned(null); setScannerOpen(true); setScannerActive(true); };

  const stopScanner = () => {
    if (readerRef.current) { try { readerRef.current.reset(); } catch {} readerRef.current = null; }
    setScannerActive(false); setScannerOpen(false); setLastScanned(null); cooldownRef.current = false;
  };

  useEffect(() => {
    if (!scannerActive || !videoRef.current) return;
    const codeReader = new BrowserMultiFormatReader();
    readerRef.current = codeReader;
    codeReader.decodeFromVideoDevice(null, videoRef.current, (result) => {
      if (result && !cooldownRef.current) {
        cooldownRef.current = true;
        const barcode = result.getText();
        const found = snacks.find(s => s.id === barcode || s.barcode === barcode || String(s.barcode) === barcode);
        if (found) { addToCart(found); setLastScanned({ name: found.id, price: found.selling_price, found: true }); }
        else { setLastScanned({ name: barcode, found: false }); }
        setTimeout(() => { cooldownRef.current = false; }, 1500);
      }
    }).catch(() => {});
    return () => { try { codeReader.reset(); } catch {} };
  }, [scannerActive, snacks]);

  const addToCart = (snack) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.snack.id === snack.id);
      if (existing) return prev.map((i) => i.snack.id === snack.id ? { ...i, qty: i.qty + 1 } : i);
      return [...prev, { snack, qty: 1 }];
    });
  };
  const updateQty = (snackId, delta) => setCart((prev) => prev.map((i) => i.snack.id === snackId ? { ...i, qty: i.qty + delta } : i).filter((i) => i.qty > 0));
  const removeFromCart = (snackId) => setCart((prev) => prev.filter((i) => i.snack.id !== snackId));
  const cartQty = (snackId) => cart.find((i) => i.snack.id === snackId)?.qty || 0;
  const totalArticles = cart.reduce((s, i) => s + i.qty, 0);
  const total = cart.reduce((sum, i) => sum + i.snack.selling_price * i.qty, 0);

  const handleCheckout = async () => {
    if (cart.length === 0) return;
    if (!clientName.trim()) { message.warning('Nom introuvable.'); return; }
    setCheckoutLoading(true);
    try {
      const productLine = cart.map((i) => `${i.qty > 1 ? `${i.qty}x ` : ''}${i.snack.id}`).join(' - ');
      await addDoc(collection(db, 'buvette'), { 'Date et Heure': Timestamp.now(), 'Nom Client': clientName.trim(), FirebaseId: firebaseId, Produit: productLine, Total: parseFloat(total.toFixed(3)) });
      setLastReceipt({ clientName: clientName.trim(), productLine, total });
      setSuccessVisible(true); setCart([]); setDrawerOpen(false);
    } catch (e) { console.error(e); message.error('Erreur lors de la commande.'); }
    finally { setCheckoutLoading(false); }
  };

  const filtered = snacks.filter((s) => s.id.toLowerCase().includes(search.toLowerCase()));
  if (!currentUser) return null;

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      <div style={{ background: '#fff', borderBottom: '1px solid #f0f0f0', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img src="/logo.PNG" alt="WeStudy" style={{ height: 38, objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
          <div>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#262626', lineHeight: 1.2 }}>Buvette</div>
            <div style={{ fontSize: '0.7rem', color: '#8c8c8c', letterSpacing: '0.06em' }}>WeStudy Private Library</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flex: 1, maxWidth: 500, justifyContent: 'flex-end' }}>
          {clientName && <div style={{ fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}><UserOutlined /> {clientName}</div>}
          <Input prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />} placeholder="Rechercher..." value={search} onChange={(e) => setSearch(e.target.value)} allowClear style={{ maxWidth: 220, width: '100%' }} />
          <Button icon={<BarcodeOutlined />} onClick={startScanner} title="Scanner un code-barres" />
          <Badge count={totalArticles} size="small">
            <Button type={totalArticles > 0 ? 'primary' : 'default'} icon={<ShoppingCartOutlined />} onClick={() => setDrawerOpen(true)} style={{ minWidth: 48 }}>
              {totalArticles > 0 && <span style={{ marginLeft: 4 }}>{total.toFixed(3)} DT</span>}
            </Button>
          </Badge>
        </div>
      </div>

      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '24px 16px 80px' }}>
        {!loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 16, fontWeight: 600, color: '#262626' }}>🥤 Notre Menu</span>
            <Tag color="blue">{filtered.length} produit{filtered.length !== 1 ? 's' : ''}</Tag>
            <Tag color="green" style={{ marginLeft: 'auto' }}>Cliquez pour ajouter au panier</Tag>
          </div>
        )}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /><p style={{ marginTop: 16, color: '#8c8c8c' }}>Chargement du menu...</p></div>
        ) : filtered.length === 0 ? <Empty description="Aucun produit trouvé" style={{ padding: 60 }} /> : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12 }}>
            {filtered.map((snack, i) => {
              const qty = cartQty(snack.id);
              return (
                <div key={snack.id} onClick={() => addToCart(snack)} style={{ borderRadius: 10, overflow: 'hidden', cursor: 'pointer', border: qty > 0 ? '2px solid #2563eb' : '1px solid #e2e8f0', background: qty > 0 ? '#eff6ff' : '#fff', boxShadow: qty > 0 ? '0 4px 16px rgba(37,99,235,0.15)' : '0 1px 4px rgba(0,0,0,0.05)', transition: 'all 0.2s', animation: `fadeUp 0.35s ease both`, animationDelay: `${i * 0.025}s`, position: 'relative' }}>
                  {qty > 0 && <div style={{ position: 'absolute', top: 6, right: 6, zIndex: 2, width: 20, height: 20, borderRadius: '50%', background: '#2563eb', color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{qty}</div>}
                  <div style={{ width: '100%', height: 130, background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', borderBottom: '1px solid #f1f5f9' }}>
                    {snack.image && !imgErrors[snack.id] ? <img src={snack.image} alt={snack.id} loading="lazy" onError={() => setImgErrors((p) => ({ ...p, [snack.id]: true }))} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 8 }} /> : <span style={{ fontSize: '2rem', opacity: 0.25 }}>🍫</span>}
                  </div>
                  <div style={{ padding: '8px 10px 10px', display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#0f172a', lineHeight: 1.35, height: 34, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{snack.id}</div>
                    <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#16a34a', background: '#f0fdf4', borderRadius: 6, padding: '2px 7px', alignSelf: 'flex-start' }}>{Number(snack.selling_price).toFixed(3)} DT</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {totalArticles > 0 && (
        <div style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 200 }} className="mobile-cart-fab">
          <Button type="primary" size="large" icon={<ShoppingCartOutlined />} onClick={() => setDrawerOpen(true)} style={{ borderRadius: 50, padding: '0 24px', boxShadow: '0 4px 16px rgba(37,99,235,0.4)' }}>
            Voir le panier · {total.toFixed(3)} DT
          </Button>
        </div>
      )}

      <Modal open={scannerOpen} onCancel={stopScanner} footer={null} title={<span><BarcodeOutlined style={{ marginRight: 8 }} />Scanner un code-barres</span>} centered width={420} destroyOnClose>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <div style={{ position: 'relative', width: '100%', borderRadius: 12, overflow: 'hidden', background: '#000' }}>
            <video ref={videoRef} style={{ width: '100%', display: 'block', maxHeight: 280, objectFit: 'cover' }} autoPlay muted playsInline />
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
              <div style={{ width: '70%', height: 2, background: 'rgba(37,99,235,0.8)', boxShadow: '0 0 8px rgba(37,99,235,0.8)', animation: 'scanLine 2s ease-in-out infinite' }} />
            </div>
            {[['top','left'],['top','right'],['bottom','left'],['bottom','right']].map(([v,h]) => (
              <div key={`${v}${h}`} style={{ position: 'absolute', [v]: 16, [h]: 16, width: 20, height: 20, borderTop: v==='top'?'3px solid #2563eb':'none', borderBottom: v==='bottom'?'3px solid #2563eb':'none', borderLeft: h==='left'?'3px solid #2563eb':'none', borderRight: h==='right'?'3px solid #2563eb':'none' }} />
            ))}
          </div>
          {lastScanned ? (
            <div style={{ width: '100%', padding: '10px 14px', borderRadius: 10, background: lastScanned.found ? '#f0fdf4' : '#fef2f2', border: `1px solid ${lastScanned.found ? '#bbf7d0' : '#fecaca'}`, display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 20 }}>{lastScanned.found ? '✅' : '❌'}</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: lastScanned.found ? '#15803d' : '#dc2626' }}>{lastScanned.found ? `${lastScanned.name} ajouté au panier` : 'Produit introuvable'}</div>
                {lastScanned.found && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{Number(lastScanned.price).toFixed(3)} DT</div>}
                {!lastScanned.found && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Code: {lastScanned.name}</div>}
              </div>
            </div>
          ) : <div style={{ fontSize: 13, color: '#64748b', textAlign: 'center' }}>Pointez la caméra vers un code-barres</div>}
          {cart.length > 0 && (
            <div style={{ width: '100%', padding: '10px 14px', borderRadius: 10, background: '#eff6ff', border: '1px solid #bfdbfe', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: '#1d4ed8' }}>🛒 {totalArticles} article{totalArticles > 1 ? 's' : ''}</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#1d4ed8' }}>{total.toFixed(3)} DT</span>
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, width: '100%' }}>
            <Button block onClick={stopScanner}>Fermer</Button>
            {cart.length > 0 && <Button block type="primary" icon={<ShoppingCartOutlined />} onClick={() => { stopScanner(); setDrawerOpen(true); }}>Voir le panier</Button>}
          </div>
        </div>
      </Modal>

      <Drawer
        title={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}><span><ShoppingCartOutlined style={{ marginRight: 8 }} />Mon Panier</span>{cart.length > 0 && <Button size="small" icon={<ClearOutlined />} danger onClick={() => setCart([])}>Vider</Button>}</div>}
        open={drawerOpen} onClose={() => setDrawerOpen(false)} placement="right" width={360}
        footer={cart.length > 0 ? (
          <div>
            <div style={{ marginBottom: 12, padding: '10px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <UserOutlined style={{ color: '#94a3b8' }} />
              <div><div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 1 }}>Commande pour</div><div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{clientName || '—'}</div></div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, marginBottom: 12 }}>
              <span style={{ fontWeight: 600, color: '#555', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 12 }}>Total</span>
              <span style={{ fontSize: '1.3rem', fontWeight: 700, color: '#16a34a' }}>{total.toFixed(3)} DT</span>
            </div>
            <Button type="primary" size="large" block icon={<CheckOutlined />} loading={checkoutLoading} onClick={handleCheckout} disabled={cart.length === 0} style={{ height: 48, fontWeight: 700, fontSize: '1rem' }}>Commander</Button>
          </div>
        ) : null}
      >
        {cart.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Votre panier est vide" style={{ marginTop: 60 }} /> : (
          <div>
            {cart.map(({ snack, qty }) => (
              <div key={snack.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ width: 44, height: 44, borderRadius: 8, background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
                  {snack.image ? <img src={snack.image} alt={snack.id} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 4 }} onError={(e) => { e.target.style.display = 'none'; }} /> : <span style={{ fontSize: '1.2rem' }}>🍫</span>}
                </div>
                <div style={{ flex: 1, fontSize: '0.82rem', fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{snack.id}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Button size="small" icon={<MinusOutlined />} onClick={() => updateQty(snack.id, -1)} style={{ width: 26, height: 26, minWidth: 26, padding: 0 }} />
                  <span style={{ width: 20, textAlign: 'center', fontWeight: 700, fontSize: 13 }}>{qty}</span>
                  <Button size="small" icon={<PlusOutlined />} onClick={() => updateQty(snack.id, 1)} style={{ width: 26, height: 26, minWidth: 26, padding: 0 }} />
                </div>
                <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#16a34a', minWidth: 64, textAlign: 'right' }}>{(snack.selling_price * qty).toFixed(3)} DT</span>
                <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeFromCart(snack.id)} style={{ padding: 0, width: 24, height: 24, minWidth: 24 }} />
              </div>
            ))}
          </div>
        )}
      </Drawer>

      <Modal open={successVisible} onOk={() => setSuccessVisible(false)} onCancel={() => setSuccessVisible(false)} okText="Fermer" cancelButtonProps={{ style: { display: 'none' } }} centered title="✅ Commande enregistrée !">
        {lastReceipt && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}><span style={{ color: '#64748b', fontSize: 13 }}>Nom</span><span style={{ fontWeight: 600, fontSize: 13 }}>{lastReceipt.clientName}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderTop: '1px solid #f1f5f9' }}><span style={{ color: '#64748b', fontSize: 13 }}>Produit(s)</span><span style={{ fontWeight: 500, fontSize: 13, textAlign: 'right', maxWidth: 200 }}>{lastReceipt.productLine}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid #f1f5f9' }}><span style={{ color: '#64748b', fontSize: 13 }}>Total</span><span style={{ fontWeight: 700, fontSize: '1.2rem', color: '#16a34a' }}>{lastReceipt.total.toFixed(3)} DT</span></div>
            <div style={{ marginTop: 8, padding: 12, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 12, color: '#92400e' }}>💡 Votre commande a été enregistrée. Veuillez vous présenter à la caisse pour régler.</div>
          </div>
        )}
      </Modal>

      <div style={{ textAlign: 'center', padding: 16, borderTop: '1px solid #f0f0f0', background: '#fff', color: '#bfbfbf', fontSize: '0.75rem' }}>© WeStudy Private Library · Tunis</div>

      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes scanLine { 0%, 100% { transform: translateY(-40px); opacity: 0.6; } 50% { transform: translateY(40px); opacity: 1; } }
        @media (max-width: 480px) { .mobile-cart-fab { display: flex !important; } }
      `}</style>
    </div>
  );
}